import Vue from 'vue';
import Vuex from 'vuex';

import defaultCharacterState from './defaultCharacterState.js';
import defaultState from './defaultState.js';

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    character: {},
    default: {}
  },
  getters: {
    getCapitalizedClasses(state) {
      let temp = state.default.characterClasses;
      return temp.map(charClass => charClass.charAt(0).toUpperCase() + charClass.substring(1, charClass.length));
    },
    getAlignment(state) {
      return state.character.alignment;
    },
    getPossibleMoves: (state) => (characterClass) => {
      let result = {
        basic: [],
        special: [],
        starting: [],
        advanced: [],
        expert: []
      };
      let moves = state.default.moves;
      moves.basic.forEach(move => result.basic.push(move));
      moves.special.forEach(move => result.special.push(move));
      moves[characterClass].starting.forEach(move => result.starting.push(move));
      moves[characterClass].advanced.forEach(move => result.advanced.push(move));
      moves[characterClass].expert.forEach(move => result.expert.push(move));
      return result;
    },
    getNormalMoves: (state) => {
      let result = {
        basic: [],
        special: []
      }
      let moves = state.default.moves;
      moves.basic.forEach(move => result.basic.push(move));
      moves.special.forEach(move => result.special.push(move));
      return result;
    },
    getCharacterMoves: (state) => {
      let result = {
        basic: [],
        special: [],
        starting: [],
        advanced: [],
        expert: []
      };
      let possibleMoves = state.default.moves;
      let moves = state.character.moves;
      possibleMoves.basic.forEach(move => result.basic.push(move));
      possibleMoves.special.forEach(move => result.special.push(move));
      // possibleMoves[characterClass.toLowerCase()].starting.forEach(move => result.starting.push(move));
      moves.starting.forEach(move => {
        result.starting.push(move);
      });
      moves.advanced.forEach(move => {
        if (move.have) {
          result.advanced.push(move);
        }
      });
      moves.expert.forEach(move => {
        if (move.have) {
          result.expert.push(move);
        }
      });
      return result;
    },
    ifSpellCharacter: (state) => {
      let type = state.character.class
      if (type) {
        return type === "cleric" || type === "wizard";
      } else {
        return false;
      }
    },
    getPossibleSpells: (state) => (characterClass) => {
      // Right now, the possible spells don't match what the character has
      // (don't have a checkmark), we can fix this, but functionally, 
      // it has no problems. It's more of a visual thing.

      return state.default.classSepcs[characterClass].spells ?
        state.default.classSepcs[characterClass].spells : null;
    },
    getCharacterSpells: (state) => {
      if (state.character.class) {
        if (Object.keys(state.character.spells).length > 0) {
          return state.character.spells;
        } else {
          return {};
        }
      } else {
        return null;
      }
    }
  },
  mutations: {
    updateCharacterState(state, value) {
      state.character = value;
    },
    updateCharacterProperty(state, values) {
      state.character[values.type] = values.property;
    },
    // Features
    updateCharacterLookBody(state, body) {
      state.character.look.body = body;
    },
    updateCharacterLookEyes(state, eyes) {
      state.character.look.eyes = eyes;
    },
    updateCharacterLookHair(state, hair) {
      state.character.look.hair = hair;
    },
    updateCharacterLookClothes(state, clothes) {
      state.character.look.clothes = clothes;
    },
    updateCharacterRace(state, race) {
      state.character.race = race;
    },

    // Stats
    updateCharacterSkills(state, skills) {
      state.character.skills = skills;
    },
    updateCharacterHP(state, hp) {
      state.character.hp.current = hp;
    },

    // Moves
    updateCharacterMoves(state, changingMove) {
      let index = 0;
      let found = state.character.moves[changingMove.type].find(move => {
        if (move.title === changingMove.title) {
          return move.title === changingMove.title
        } else {
          index++;
        }
      });
      if (found) {
        // if we find the move, update the move and filter the rest out that have changed
        state.character.moves[changingMove.type][index] = changingMove;
        state.character.moves[changingMove.type] = state.character.moves[changingMove.type].filter(move => move.have || move.have === null);
      } else if (changingMove.have) {
        state.character.moves[changingMove.type].push(changingMove);
      }
    },
    updateCharacterSpell(state, spell) {
      let spellList = state.character.spells;
      let index, found = false;
      if (Object.keys(spellList).length > 0) {
        found = spellList[spell.type].find((spellers, i) => {
          index = i;
          return spellers.title === spell.title;
        });
      }

      if (found) {
        state.character.spells[spell.type][index] = spell;
      } else {
        if (!state.character.spells[spell.type]) {
          state.character.spells[spell.type] = [];
        }
        state.character.spells[spell.type].push(spell);
      }
      state.character.spells[spell.type] = state.character.spells[spell.type].filter(spellers => spellers.have);
    },

    // Gear
    updateCharacterCoin(state, coin) {
      state.character.coin = coin;
    },
    updateCharacterWeight(state, weight) {
      state.character.weight = weight;
    },
    updateCharacterInventory(state, inventory) {
      state.character.inventory = inventory;
    },

    // Notes
    updateCharacterNotes(state, notes) {
      notes.map(note => {
        note.component = note.component.name;
        return note;
      })
      state.character.notes = notes;
    },

    // Save
    saveCharacter(state) {
      localStorage.setItem('dungeonWorldCharacterBuilder', JSON.stringify(state.character));
    },
    loadCharacter(state, localCharacter) {
      let character = JSON.parse(localCharacter);
      Object.keys(state.character).forEach(key => {
        state.character[key] = character[key];
      });

      // If we don't have the starting moves in the character, set them
      if (state.character.moves.starting.length === 0 && state.character.class) {
        state.default.moves[state.character.class].starting.forEach(move => {
          state.character.moves.starting.push(move);
        })
      }
    }
  },
  actions: {
    init({ state }) {
      state.character = defaultCharacterState;
      state.default = defaultState;
    },
    updateClassRelatedAttributes({ state }, newClass) {
      state.character.damage = state.default.classSepcs[newClass].damage;
      state.character.maxWeight = state.default.classSepcs[newClass].maxWeight;
    },
    updateCharacterClass({ state }, charClass) {
      if (charClass === 'wizard' || charClass === 'cleric') {
        state.character.spells = {};
      } else {
        state.character.spells = null;
      }
      state.character.class = charClass;
      state.character.moves.starting = [...state.default.moves[charClass].starting];
      state.character.moves.advanced = [];
      state.character.moves.expert = [];
      state.character.maxWeight = state.default.classSepcs[charClass].maxWeight;
      state.character.damage = state.default.classSepcs[charClass].damage;
    },
    clearCharacterFeatures({ state }) {
      state.character.name = '';
      state.character.class = '';
      state.character.level = 1;
      state.character.xp = 0;
      state.character.look.body = '';
      state.character.look.eyes = '';
      state.character.look.hair = '';
      state.character.look.clothes = '';
      state.character.alignment = '';
      state.character.bonds = [];
      state.character.race = { title: '', description: '' };
      state.character.moves.starting = []
      state.character.moves.advanced = []
      state.character.moves.expert = []
    },
    clearCharacterStats(state) {
      state.commit('updateCharacterSkills', [
        { name: 'Strength', abreviation: 'STR', score: 0 },
        { name: 'Dexterity', abreviation: 'DEX', score: 0 },
        { name: 'Constitution', abreviation: 'CON', score: 0 },
        { name: 'Inteligence', abreviation: 'INT', score: 0 },
        { name: 'Wisdom', abreviation: 'WIS', score: 0 },
        { name: 'Charisma', abreviation: 'CHA', score: 0 },
      ]);
      state.state.character.armor = 0;
      state.state.character.hp = { max: 0, current: 0 };
    },
    clearCharacterMoves({ state }) {
      state.character.moves = {
        basic: [],
        special: [],
        starting: [],
        advanced: [],
        expert: []
      };
    },
    clearCharacterGear({ state }) {
      state.character.coin = 0;
      state.character.weight = 0;
      state.character.inventory = 0;
    },
    clearCharacterNotes({ state }) {
      state.character.notes = [];
    }
  }
});

export default store;